import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { trackVisit } from './trackVisitor';
import { TeamProvider } from './contexts/TeamContext';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import CreateSchedule from './pages/CreateSchedule';
import PendingApproval from './pages/PendingApproval';
import RequestsPage from './pages/RequestsPage';
import ProfilePage from './pages/ProfilePage';
import SetupTeam from './pages/SetupTeam';
import BottomNav from './components/BottomNav';
import './App.css';

// Protected Route
function ProtectedRoute({ children, requiredRole, user, userData }) {
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (userData?.role === 'pending' && location.pathname !== '/pending') {
    return <Navigate to="/pending" replace />;
  }

  // If no teamId → redirect to setup-team (except for /setup-team itself)
  if (user && userData && userData.role !== 'pending' && !userData.teamId && location.pathname !== '/setup-team') {
    return <Navigate to="/setup-team" replace />;
  }

  if (requiredRole === 'admin' && userData?.teamRole !== 'owner' && userData?.teamRole !== 'admin' && userData?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent({ user, userData }) {
  const location = useLocation();
  const showBottomNav = user && userData?.role !== 'pending' && userData?.teamId &&
    !['/login', '/pending', '/setup-team'].includes(location.pathname);

  return (
    <>
      <div className={`app-container${showBottomNav ? ' app-container--with-nav' : ''}`}>
        <Routes>
          <Route
            path="/login"
            element={
              user && userData?.role !== 'pending'
                ? (userData?.teamId ? <Navigate to="/" /> : <Navigate to="/setup-team" />)
                : <Login />
            }
          />
          <Route path="/pending" element={<PendingApproval />} />
          <Route
            path="/setup-team"
            element={
              user && userData?.role !== 'pending'
                ? (userData?.teamId ? <Navigate to="/" /> : <SetupTeam user={user} userData={userData} />)
                : <Navigate to="/login" replace />
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute user={user} userData={userData}>
                <Dashboard user={user} userData={userData} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <ProtectedRoute user={user} userData={userData}>
                <RequestsPage user={user} userData={userData} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={
              <ProtectedRoute user={user} userData={userData}>
                <CreateSchedule user={user} userData={userData} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit/:id"
            element={
              <ProtectedRoute user={user} userData={userData}>
                <CreateSchedule user={user} userData={userData} editMode />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute user={user} userData={userData}>
                <ProfilePage user={user} userData={userData} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} userData={userData} requiredRole="admin">
                <Admin user={user} userData={userData} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
      {showBottomNav && <BottomNav userData={userData} />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const docRef = doc(db, 'users', currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUserData(docSnap.data());
        } else {
          setUserData({ role: 'pending' });
        }
        trackVisit(currentUser);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-screen">
        <span className="spinner"></span>
        Ачаалж байна...
      </div>
    );
  }

  return (
    <Router>
      <TeamProvider user={user} userData={userData}>
        <AppContent user={user} userData={userData} />
      </TeamProvider>
    </Router>
  );
}

export default App;
